import DOMPurify from 'dompurify';
import React, { forwardRef, useId, useRef } from 'react';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';

export interface CheckboxChangeEventDetail {
  checked: boolean;
  value: string;
  nativeEvent: Event | undefined;
}

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  /** Sets the text description of the checkbox (can be a string or ReactNode for links) */
  label?: string | React.ReactNode;
  /** Sets the component in error state */
  error?: boolean;
  /** Toggles between checked and unchecked state */
  checked?: boolean;
  /** Sets the default checked state of the form control. */
  defaultChecked?: boolean;
  /** Toggles between disabled and enabled state */
  disabled?: boolean;
  /** Sets name of the element */
  name?: string;
  /** Makes the value non-editable */
  readonly?: boolean;
  /** Sets the component value */
  value?: string;
  /** Sets the checkbox mixed state and overrides the checked and value properties */
  intermediate?: boolean;
  /** Sets whether the checkbox should be required or not */
  required?: boolean;
  /** Sets the size of the checkbox */
  size?: 'small' | 'medium';
  /** Custom className for styling */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
  /** Change event handler with detail object similar to VCDK */
  onChange?: (event: CustomEvent<CheckboxChangeEventDetail>) => void;
}

/**
 * Checkbox component - Clone of @volvo/vcdk-react Checkbox
 *
 * Checkboxes allow users to toggle options.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error = false,
      checked,
      defaultChecked,
      disabled = false,
      name,
      readonly = false,
      value = '',
      intermediate = false,
      required = false,
      size = 'medium',
      className = '',
      style,
      onChange,
      ...rest
    },
    ref
  ) => {
    const uniqueId = useId();
    const checkboxId = `checkbox-${uniqueId}`;

    // SAFETY: label activation forwards clicks on non-native elements (vcdk-link custom element)
    // to the input, so clicking a link in the label would also toggle the checkbox. preventDefault
    // can't be used — it would cancel href navigation — so flag link clicks and swallow the
    // forwarded change instead. The timeout clears the flag when no change follows (native anchors
    // are skipped by label activation and never forward one).
    const suppressNextChange = useRef(false);
    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
      if ((event.target as Element).closest('a, vcdk-link')) {
        suppressNextChange.current = true;
        setTimeout(() => {
          suppressNextChange.current = false;
        });
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (suppressNextChange.current) {
        suppressNextChange.current = false;
        event.target.checked = !event.target.checked;
        return;
      }
      if (onChange) {
        const customEvent = new CustomEvent<CheckboxChangeEventDetail>('change', {
          detail: {
            checked: event.target.checked,
            value: value,
            nativeEvent: event.nativeEvent,
          },
        }) as CustomEvent<CheckboxChangeEventDetail>;
        onChange(customEvent);
      }
    };

    const sizeClass = size === 'small' ? 'text-sm' : 'text-base';
    const errorClass = error ? 'border-error' : 'border-gray-300';
    const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';

    return (
      <div className={`checkbox flex items-start gap-2 ${className}`} style={style}>
        <div>
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            name={name}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            readOnly={readonly}
            required={required}
            value={value}
            onChange={handleChange}
            className={`relative z-10 mt-2 ml-2 size-[18px] rounded ${errorClass} ${disabledClass} `}
            aria-checked={intermediate ? 'mixed' : checked}
            style={{
              accentColor: '#212121',
            }}
            {...rest}
          />
        </div>
        {label && (
          <label htmlFor={checkboxId} onClick={handleLabelClick} className={`px-5 ${sizeClass} ${disabledClass} select-none`}>
            {typeof label === 'string' ? (
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label) }} />
            ) : (
              label
            )}
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default withComponentErrorBoundary(Checkbox);
